import { createToaster } from '@chakra-ui/react'
import { CustomToast } from 'stylewhere/components'
import { AppStore, Sounds } from 'stylewhere/shared'
import { SystemError } from 'stylewhere/shared/errors'
import { T, __ } from 'stylewhere/shared/i18n'

interface ToastOptions {
  sound?: boolean
  status: 'success' | 'error' | 'warning' | 'info'
  title?: string | React.ReactNode
  description?: string | React.ReactNode
  position?: 'top' | 'top-start' | 'top-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'bottom-right'
  duration?: number
  isClosable?: boolean
  render?: (props: { id: string | number; onClose: () => void }) => React.ReactNode
}

export const toaster = createToaster({
  placement: 'bottom-end',
  pauseOnPageIdle: true
})

export const hideToast = (id: string | number) => {
  toaster.dismiss(String(id))
}

export const showToast = ({
  sound = false,
  position = 'bottom-end',
  duration = 3000,
  status,
  isClosable = true,
  ...options
}: ToastOptions) => {
  switch (status) {
    case 'error':
      if (sound) Sounds.error()
      duration = 8000
      break
    case 'warning':
      if (sound) Sounds.error()
      duration = 4000
      break
    case 'success':
      if (sound) Sounds.success()
      break
  }

  // Secondo la documentazione di Chakra UI, il placement è configurato nel createToaster
  // e non può essere sovrascritto per singoli toast. Se necessario, creare toaster multipli.
  // Per ora, il placement è configurato nel createToaster come 'bottom-end'

  // Passa render come parte delle opzioni del toast se presente
  const toastOptions: any = {
    type: status,
    duration,
    title: options.title as string,
    description: options.description as string
  }

  // Se c'è un render custom, salvalo nelle opzioni
  if (options.render) {
    toastOptions.render = options.render
  }

  return toaster.create(toastOptions)
}

export function openFeedbackModal(
  title: string,
  message: string,
  onModalClose?: () => void,
  btnCloseModal?
): Promise<boolean> {
  return new Promise((resolve) => {
    AppStore.showFeedbackModal(title, message, () => (onModalClose ? onModalClose() : resolve(false)), btnCloseModal)
  })
}

export function hideFeedbackModal() {
  AppStore.hideFeedbackModal()
}

export const showToastError = (
  err: any,
  title: string = __(T.error.error),
  modal = false,
  onModalClose?: () => void,
  btnCloseModal = __(T.misc.ok),
  playSound = true
) => {
  if (modal) {
    let message = 'Unknown error'
    if (err instanceof Error) {
      message = err.message ? err.message : 'Unknown error'
    } else if (typeof err === 'string') {
      message = err
    }
    return openFeedbackModal(title, message, onModalClose, btnCloseModal)
  }
  if (err instanceof Error) {
    if (err instanceof SystemError || err?.name === 'SystemError') {
      return showToast({
        sound: playSound,
        title,
        status: 'error',
        render: ({ id, onClose }) => (
          <CustomToast
            title={(err as SystemError).title ?? __(T.error.be_generic_error)}
            description={(err as SystemError).description ?? __(T.error.be_generic_error_description)}
            err={err as SystemError}
            id={id}
            onClose={onClose}
          />
        )
      })
    }
    const description = err.message ? err.message : 'Unknown error'
    return showToast({
      sound: playSound,
      title,
      description,
      status: 'error'
    })
  }
  if (typeof err === 'string') {
    return showToast({
      sound: playSound,
      title,
      description: err,
      status: 'error'
    })
  }
  return showToast({
    sound: playSound,
    title,
    description: 'Generic',
    status: 'error'
  })
}
